import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { UserFacadeService } from 'src/app/auth/state/user-facade.service';
import { SnackbarService } from './snackbar.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  private messaging = inject(Messaging);
  private userFacade = inject(UserFacadeService);
  private snackbar = inject(SnackbarService);
  private environmentInjector = inject(EnvironmentInjector);
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

  /**
   * Gets the existing service worker registration that Angular has already registered.
   * Our main service worker already imports the Firebase messaging code.
   */
  private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (this.serviceWorkerRegistration) {
      return this.serviceWorkerRegistration;
    }

    if ('serviceWorker' in navigator) {
      try {
        // Get the existing service worker registration that Angular has already created
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          this.serviceWorkerRegistration = registration;
          return registration;
        }
      } catch (error) {
        console.warn('Error getting existing service worker registration:', error);
      }
    }

    return null;
  }

  /** ✅ Requests notification permission & retrieves the token */
  async requestPermission(): Promise<void> {
    try {
      const permission = await Notification.requestPermission();
      // console.log(`Notification permission: ${permission}`);

      if (permission === 'granted') {
        const serviceWorkerRegistration = await this.getServiceWorkerRegistration();

        const tokenOptions: any = {
          vapidKey: environment.vapidKey,
        };

        // If we have an existing service worker registration, use it
        // Otherwise, let Firebase Messaging use its default registration logic
        if (serviceWorkerRegistration) {
          tokenOptions.serviceWorkerRegistration = serviceWorkerRegistration;
        }

        const token = await runInInjectionContext(
          this.environmentInjector,
          async () => await getToken(this.messaging, tokenOptions)
        );
        if (token) {
          // console.log('FCM Token:', token);
          await this.userFacade.saveNotificationToken(token);
        } else {
          console.warn('No FCM token received.');
        }
      } else {
        console.warn('Notification permission denied.');
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
    }
  }

  /** ✅ Listens for incoming push messages */
  receiveMessage(): void {
    runInInjectionContext(this.environmentInjector, () => {
      onMessage(this.messaging, (payload) => {
        console.log('📩 Message received:', payload);
        const remoteMsg = payload?.notification?.body ?? '';
        if (remoteMsg) {
          this.snackbar.info(remoteMsg);
        }
      });
    });
  }
}
